/**
 * The professional 2FA wire contract, as pure logic.
 *
 * NO apiClient IMPORT, deliberately. The predicates below decide whether a
 * login carries tokens or a challenge, and which 403 means what — the kind of
 * logic that most needs cases and least needs a network. Importing the client
 * drags in `import.meta.env`, which does not exist outside Vite, so a spec
 * could not load it at all. authService and totpService re-export these, so
 * call sites are unchanged.
 *
 * Spec 5d9018f9.
 */

/** A professional password login awaiting its authenticator code. */
export type ProfessionalTotpRequiredResponse = {
  step: "totp_required";
  pendingLoginId: string;
  expiresAt: string;
};

/** The machine-readable codes the two guards answer with. */
export const TOTP_ENROLLMENT_REQUIRED = "totp_enrollment_required";
export const TOTP_STEP_UP_REQUIRED = "totp_step_up_required";

export function isTotpRequiredResponse(
  r: unknown
): r is ProfessionalTotpRequiredResponse {
  return (
    typeof r === "object" &&
    r != null &&
    (r as ProfessionalTotpRequiredResponse).step === "totp_required"
  );
}

/**
 * Whether a SUCCESSFUL login still owes us enrollment.
 *
 * The backend issues a session to an un-enrolled professional on purpose — the
 * enrollment endpoints require one — and flags it here. Without acting on the
 * flag a client would discover the obligation only by collecting a 403 from
 * whatever it tried next.
 */
export function requiresTotpEnrollment(r: unknown): boolean {
  return (
    typeof r === "object" &&
    r != null &&
    (r as { totpEnrollmentRequired?: boolean }).totpEnrollmentRequired === true
  );
}

function errorCode(error: unknown): string | null {
  const data = (error as { response?: { data?: { code?: unknown } } })?.response?.data;
  return typeof data?.code === "string" ? data.code : null;
}

/** A 403 meaning "configure your authenticator first". Routes to setup. */
export function isTotpEnrollmentRequiredError(error: unknown): boolean {
  return errorCode(error) === TOTP_ENROLLMENT_REQUIRED;
}

/**
 * A 403 meaning "confirm a code to continue".
 *
 * Answered where the action was attempted, NOT by a redirect — bouncing
 * someone off the payout page would lose what they were doing.
 */
export function isTotpStepUpRequiredError(error: unknown): boolean {
  return errorCode(error) === TOTP_STEP_UP_REQUIRED;
}
